import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AiSalesRecommendationRecord, SalesAiApiService } from './sales-ai-api.service';
import { SalesAiOutputViewComponent } from './sales-ai-output-view.component';

@Component({
  standalone: true,
  selector: 'ain-sales-ai-copilot-panel',
  imports: [DecimalPipe, RouterLink, FormsModule, ButtonModule, InputTextModule, TextareaModule, AinStateComponent, SalesAiOutputViewComponent],
  template: `
    <aside class="copilot" [class.collapsed]="collapsed()">
      <header>
        <strong>{{ t('sales.ai.copilot.title') }}</strong>
        <button type="button" class="toggle" (click)="collapsed.set(!collapsed())">
          {{ collapsed() ? t('sales.ai.copilot.expand') : t('sales.ai.copilot.collapse') }}
        </button>
      </header>
      @if (!collapsed()) {
        <p class="hint">{{ t('sales.ai.copilot.advisoryOnly') }}</p>
        @if (!entityId()) {
          <ain-state type="empty" [title]="t('sales.ai.copilot.noEntity')" />
        } @else if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else {
          <div class="actions">
            <button pButton type="button" size="small" [label]="t('sales.ai.copilot.refresh')" (click)="load()"></button>
            <button pButton type="button" size="small" severity="secondary" [label]="t('sales.ai.copilot.suggest')" [disabled]="suggesting()" (click)="suggest()"></button>
          </div>
          @for (row of recommendations(); track row.id) {
            <article class="rec">
              <div class="meta">
                <span class="type">{{ row.recommendationType }}</span>
                <span class="score">{{ (row.score * 100) | number:'1.0-0' }}%</span>
              </div>
              <p>{{ row.explanation?.summary || t('sales.ai.copilot.noExplanation') }}</p>
              @if (row.outputJson && row.outputJson !== '{}') {
                <ain-sales-ai-output-view [outputJson]="row.outputJson" />
              }
              <a [routerLink]="['/sales/ai-recommendations', row.id]">{{ t('sales.ai.copilot.viewDetail') }}</a>
            </article>
          } @empty {
            <p class="empty">{{ t('sales.ai.copilot.empty') }}</p>
          }
          <section class="note-box">
            <label>{{ t('sales.ai.copilot.noteLabel') }}</label>
            <textarea pTextarea rows="3" [(ngModel)]="noteText"></textarea>
            <button pButton type="button" size="small" [label]="t('sales.ai.copilot.saveNote')" [disabled]="!noteText.trim()" (click)="saveNote()"></button>
          </section>
        }
      }
    </aside>
  `,
  styles: [`
    .copilot { border: 1px solid #dbeafe; border-radius: 12px; padding: 12px; background: #f8fafc; min-width: 260px; }
    .copilot.collapsed { min-width: auto; }
    header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 8px; }
    .toggle { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-size: 12px; }
    .hint { margin: 0 0 8px; font-size: 12px; color: #6b7280; }
    .actions { display: flex; gap: 6px; margin-bottom: 8px; flex-wrap: wrap; }
    .rec { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; margin-bottom: 8px; background: #fff; }
    .meta { display: flex; justify-content: space-between; font-size: 12px; color: #374151; margin-bottom: 4px; }
    .type { font-weight: 600; }
    .score { color: #1d4ed8; }
    .rec p { margin: 0 0 6px; font-size: 13px; }
    .rec a { font-size: 12px; color: #1d4ed8; }
    .empty { font-size: 13px; color: #6b7280; }
    .note-box { display: grid; gap: 6px; margin-top: 10px; }
    label { font-size: 12px; color: #6b7280; }
  `]
})
export class SalesAiCopilotPanelComponent implements OnInit {
  private readonly api = inject(SalesAiApiService);
  private readonly localization = inject(LocalizationService);

  readonly entityType = input.required<string>();
  readonly entityId = input<string | null | undefined>(null);
  readonly defaultRecommendationType = input('NextBestAction');

  readonly loading = signal(false);
  readonly suggesting = signal(false);
  readonly collapsed = signal(false);
  readonly recommendations = signal<AiSalesRecommendationRecord[]>([]);
  noteText = '';

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    const id = this.entityId();
    if (!id) return;
    this.loading.set(true);
    this.api.listRecommendations({ entityType: this.entityType(), entityId: id }).subscribe({
      next: rows => { this.recommendations.set(rows.slice(0, 5)); this.loading.set(false); },
      error: () => { this.recommendations.set([]); this.loading.set(false); }
    });
  }

  suggest(): void {
    const id = this.entityId();
    if (!id) return;
    this.suggesting.set(true);
    const type = this.defaultRecommendationType();
    this.api.suggestCopilot({
      recommendationType: type,
      entityType: this.entityType(),
      entityId: id,
      contextJson: JSON.stringify({ entityType: this.entityType(), entityId: id, source: 'copilot-panel' })
    }).subscribe({
      next: () => { this.suggesting.set(false); this.load(); },
      error: () => this.suggesting.set(false)
    });
  }

  saveNote(): void {
    const id = this.entityId();
    if (!id || !this.noteText.trim()) return;
    this.api.createConversation({
      entityType: this.entityType(),
      entityId: id,
      role: 'user',
      content: this.noteText.trim()
    }).subscribe({
      next: () => { this.noteText = ''; }
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
